import { useState, useEffect } from 'react';
import { Button, message } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import { useAuth } from './auth/AuthContext';
import { apiClient } from '../../api/config';
import { logger } from '../../utils/logger';
import AIUpgradePrompt from '../../shared/components/AIUpgradePrompt';

// AI 限制定義
const AI_LIMITS = {
  'guest':   4,    // 未登入用戶
  'free' :  20,
  'pro'  : 200,
  'pro+' : 200
};

// 创建一个单例模式的实例来跨组件共享状态
let instance = null;

class AIButtonLimitManager {
  constructor() {
    if (instance) {
      return instance;
    }
    
    this.cache = {
      count: 0,
      lastUpdated: null,
      month: null,
      access_tier: 'free'
    };
    this.listeners = [];
    instance = this;
    
    // 初始化缓存
    this.loadCache();
  }

  loadCache() {
    try {
      const cached = localStorage.getItem('aiButtonLimitCache');
      if (cached) {
        const parsed = JSON.parse(cached);
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const cacheMonth = parsed.month;
        
        // 如果是同一個月，使用緩存
        if (cacheMonth && cacheMonth.year === currentYear && cacheMonth.month === currentMonth) {
          this.cache = {
            ...parsed,
            access_tier: parsed.access_tier || 'free'
          };
        } else {
          // 不同月份，重置緩存
          this.cache = {
            count: 0,
            lastUpdated: null,
            month: { year: currentYear, month: currentMonth },
            access_tier: 'free'
          };
          this.saveCache();
        }
      }
    } catch (error) {
      logger.error('載入 AI 按鈕限制緩存失敗:', error);
      this.cache = {
        count: 0,
        lastUpdated: null,
        month: { year: new Date().getFullYear(), month: new Date().getMonth() },
        access_tier: 'free'
      };
    }
  }

  saveCache() {
    try {
      localStorage.setItem('aiButtonLimitCache', JSON.stringify(this.cache));
    } catch (error) {
      logger.error('保存 AI 按鈕限制緩存失敗:', error);
    }
  }

  // 獲取未登入用戶的查詢次數
  getGuestUsageCount() {
    try {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const cacheKey = `guest_ai_usage_${currentYear}_${currentMonth}`;
      
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const data = JSON.parse(cached);
        // 檢查是否是當月數據
        if (data.month === currentMonth && data.year === currentYear) {
          return data.count || 0;
        }
      }
      return 0;
    } catch (error) {
      logger.error('獲取未登入用戶查詢次數失敗:', error);
      return 0;
    }
  }

  // 增加未登入用戶的查詢次數
  incrementGuestUsage() {
    try {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const cacheKey = `guest_ai_usage_${currentYear}_${currentMonth}`;
      
      const currentCount = this.getGuestUsageCount();
      const newCount = currentCount + 1;
      
      const data = {
        count: newCount,
        month: currentMonth,
        year: currentYear,
        lastUpdated: new Date().toISOString()
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(data));
      return newCount;
    } catch (error) {
      logger.error('增加未登入用戶查詢次數失敗:', error);
      return 0;
    }
  }

  async fetchAICountFromDB(userId) {
    try {
      const response = await apiClient.get(`/api/users/ai-action-count/${userId}`);
      return response.data.count || 0;
    } catch (error) {
      logger.error('從資料庫獲取 AI 點擊次數失敗:', error);
      return 0;
    }
  }

  async fetchAccessTierFromDB(userId) {
    try {
      const response = await apiClient.get(`/api/users/access-tier/${userId}`);
      return response.data.access_tier || 'free';
    } catch (error) {
      logger.error('從資料庫獲取 access_tier 失敗:', error);
      return 'free';
    }
  }

  // 檢查 AI 限制（整合版本）
  async checkAILimit(userId, isAuthenticated) {
    // 未登入用戶處理
    if (!isAuthenticated || !userId) {
      const currentCount = this.getGuestUsageCount();
      const limit = AI_LIMITS['guest'];
      
      // 檢查是否已達到限制
      if (currentCount >= limit) {
        const limitData = {
          currentCount,
          limit,
          accessTier: 'guest',
          isLimitReached: true
        };
        
        return {
          canProceed: false,
          reason: '已達到未登入用戶 AI 查詢次數上限，請登錄以獲得更多查詢次數',
          limitInfo: limitData
        };
      }

      // 增加查詢次數
      const newCount = this.incrementGuestUsage();
      
      logger.info(`未登入用戶 AI 按鈕點擊成功: ${newCount}/${limit}, 剩餘: ${limit - newCount}`);
      
      return {
        canProceed: true,
        currentCount: newCount,
        limit,
        remaining: limit - newCount,
        accessTier: 'guest'
      };
    }

    // 已登入用戶處理
    try {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();

      // 每次都從資料庫獲取最新次數與方案
      const [dbCount, fetchedAccessTier] = await Promise.all([
        this.fetchAICountFromDB(userId),
        this.fetchAccessTierFromDB(userId)
      ]);

      // 同步到本地緩存（不做本地自增）
      this.cache.count = dbCount;
      this.cache.access_tier = fetchedAccessTier;
      this.cache.lastUpdated = new Date().toISOString();
      this.cache.month = { year: currentYear, month: currentMonth };
      this.saveCache();
      const effectiveCount = dbCount;

      // 檢查是否超過限制
      const accessTier = this.cache.access_tier || 'free';
      const limit = AI_LIMITS[accessTier] || AI_LIMITS['free'];
      const isLimitReached = effectiveCount >= limit;

      this.notifyListeners();

      if (isLimitReached) {
        const limitData = {
          currentCount: effectiveCount,
          limit,
          accessTier: accessTier,
          isLimitReached: true
        };
        
        return {
          canProceed: false,
          reason: '已達到本月 AI 查詢次數上限',
          limitInfo: limitData
        };
      }

      logger.info(`AI 按鈕點擊成功: ${effectiveCount}/${limit}, 剩餘: ${limit - effectiveCount}, 方案: ${accessTier}`);

      // 不做本地 +1，單一權威來源為資料庫回傳的次數
      return {
        canProceed: true,
        currentCount: effectiveCount,
        limit,
        remaining: limit - effectiveCount,
        accessTier: accessTier
      };
    } catch (error) {
      logger.error('檢查 AI 限制失敗:', error);
      return {
        canProceed: false,
        reason: '檢查 AI 限制時發生錯誤，請稍後再試'
      };
    }
  }

  getCurrentCount() {
    return this.cache.count;
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener(this.cache));
  }

  reset() {
    this.cache = {
      count: 0,
      lastUpdated: null,
      month: { year: new Date().getFullYear(), month: new Date().getMonth() },
      access_tier: 'free'
    };
    this.saveCache();
    this.notifyListeners();
  }
}

// 主要的 Hook - 整合了 useAILimit 和 useAIButtonLimit 的功能
export const useAILimit = () => {
  const { user, isAuthenticated } = useAuth();
  const [manager] = useState(() => new AIButtonLimitManager());
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // 檢查 AI 限制
  const checkAILimit = async (actionType) => {
    try {
      const result = await manager.checkAILimit(user?.user_id, isAuthenticated);
      
      if (!result.canProceed) {
        setLimitInfo(result.limitInfo);
        setShowUpgradePrompt(true);
        return result;
      }

      setLimitInfo({
        currentCount: result.currentCount,
        limit: result.limit,
        accessTier: result.accessTier,
        isLimitReached: false
      });

      return result;
    } catch (error) {
      logger.error('檢查 AI 限制失敗:', error);
      return {
        canProceed: false,
        reason: '檢查 AI 限制時發生錯誤，請稍後再試'
      };
    }
  };

  // 創建 AI 按鈕的函數
  const createAIButton = ({ 
    onAIClick, 
    buttonText = "AI 分析",
    buttonProps = {},
    actionType = 'AI_BUTTON_CLICK',
    showSuccessMessage = true
  }) => {
    const handleAIClick = async () => {
      try {
        setIsLoading(true);
        
        // 檢查 AI 按鈕限制
        const result = await checkAILimit(actionType);
        
        if (!result.canProceed) {
          if (result.reason) {
            message.warning(result.reason);
          }
          return;
        }

        // 顯示當前使用情況
        if (showSuccessMessage && result.currentCount !== undefined) {
          const { currentCount, limit, remaining, accessTier } = result;
          const userType = accessTier === 'guest' ? '未登入用戶' : '已登入用戶';
          message.success(`${userType} AI 分析已開始 (${currentCount}/${limit}, 剩餘: ${remaining})`);
        }

        // 執行 AI 點擊回調
        if (onAIClick) {
          await onAIClick(result);
        }

      } catch (error) {
        console.error('AI 按鈕點擊失敗:', error);
        message.error('AI 功能暫時不可用，請稍後再試');
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <>
        <Button
          type="primary"
          icon={<RobotOutlined />}
          loading={isLoading}
          onClick={handleAIClick}
          {...buttonProps}
        >
          {buttonText}
        </Button>

        <AIUpgradePrompt
          visible={showUpgradePrompt}
          onCancel={() => setShowUpgradePrompt(false)}
          limitInfo={limitInfo}
        />
      </>
    );
  };

  return {
    checkAILimit,
    showUpgradePrompt,
    setShowUpgradePrompt,
    limitInfo,
    AI_LIMITS,
    createAIButton,
    isLoading
  };
};

// 兼容舊版本的 Hook
export const useAIButtonLimit = () => {
  const { user, isAuthenticated } = useAuth();
  const [manager] = useState(() => new AIButtonLimitManager());
  const [count, setCount] = useState(manager.getCurrentCount());
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  
  useEffect(() => {
    // 订阅变化
    const unsubscribe = manager.subscribe(cache => {
      setCount(cache.count);
    });
    
    return unsubscribe;
  }, [manager]);

  // 检查AI按钮限制
  const checkAILimit = async () => {
    if (!isAuthenticated || !user) {
      setShowUpgradePrompt(true);
      return { canProceed: false, reason: '請先登錄' };
    }

    try {
      const result = await manager.checkAILimit(user.user_id, isAuthenticated);
      setLimitInfo(result.limitInfo);
      
      if (result.canProceed) {
        return { 
          canProceed: true, 
          limitInfo: result.limitInfo
        };
      } else {
        setShowUpgradePrompt(true);
        return { 
          canProceed: false, 
          reason: result.reason,
          limitInfo: result.limitInfo
        };
      }
    } catch (error) {
      logger.error('檢查 AI 按鈕限制失敗:', error);
      return { canProceed: false, reason: '檢查限制時發生錯誤' };
    }
  };

  return {
    count,
    checkAILimit,
    showUpgradePrompt,
    setShowUpgradePrompt,
    limitInfo,
    isLimitReached: limitInfo?.isLimitReached || false
  };
};

// AI 按鈕限制檢查組件
export const AIButtonLimitChecker = ({ onLimitReached, children }) => {
  const { checkAILimit, showUpgradePrompt, setShowUpgradePrompt, limitInfo } = useAIButtonLimit();
  
  const handleClick = async () => {
    const result = await checkAILimit();
    
    if (!result.canProceed) {
      if (onLimitReached) {
        onLimitReached(result);
      }
      return;
    }
    
    // 如果有限制檢查組件，執行它
    if (children && typeof children === 'function') {
      children(result);
    }
  };

  return {
    handleClick,
    showUpgradePrompt,
    setShowUpgradePrompt,
    limitInfo
  };
};

// 導出 AI_LIMITS 供其他組件使用
export { AI_LIMITS };